<aside
  class="relative z-30 w-screen md:w-full h-full md:h-screen bg-brand-white flex flex-col shadow-sidebar"
>
  <!-- Logo -->
  <div
    class="hidden md:flex h-17 sticky top-0 z-20 items-center justify-between bg-brand-navBar px-4 shadow-header border-r border-brand-navBar transition-[padding,background-color] duration-300 ease-in-out"
    [ngClass]="isCollapsed ? 'px-0 justify-center' : ''"
  >
    @if (!isCollapsed) {
      <a routerLink="/" class="h-11 w-36">
        <img
          [src]="IMAGE_PATHS.MY_ACCOUNT"
          [alt]="ALT_TEXT.MY_ACCOUNT"
          class="h-full w-full object-contain filter-logo-on-navbar"
          loading="lazy"
        />
      </a>
    }
    <button
      type="button"
      class="hidden md:flex h-17 w-10 items-center justify-center cursor-pointer"
      [ngClass]="isCollapsed ? 'mx-auto' : 'shrink-0'"
      [attr.aria-label]="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      aria-controls="main-sidebar-nav"
      [attr.aria-expanded]="!isCollapsed"
      (click)="toggleCollapse.emit()"
    >
      <img
        [src]="IMAGE_PATHS.COLLAPSED"
        [alt]="ALT_TEXT.COLLAPSED"
        width="24"
        height="24"
        loading="lazy"
        class="theme-icon"
      />
    </button>
  </div>

  <!-- Navigation -->
  <nav
    id="main-sidebar-nav"
    class="flex-1 min-h-0 sidebar-bg border-r sidebar-scroll-hidden border-brand-muteShadow transition-[padding] duration-300 ease-in-out"
    [ngClass]="
      isCollapsed
        ? 'overflow-y-auto overflow-x-hidden px-3 py-6'
        : 'overflow-y-auto overflow-x-hidden px-4 py-6'
    "
  >
    <div class="md:hidden flex flex-col h-12 items-start">
      <img
        [src]="
          themeService.isDarkMode() ? IMAGE_PATHS.MY_ACCOUNT_WHITE : IMAGE_PATHS.MYACCOUNT_LOGIN
        "
        [alt]="ALT_TEXT.MY_ACCOUNT"
        class="h-8 w-36 object-contain object-left"
        loading="lazy"
      />
    </div>

    <!-- Mobile-only impersonated user info (header already shows on desktop) -->
    @if (partySearchContext.impersonatedUserApiData()) {
      <div class="mb-4 mt-3 md:hidden">
        <p class="text-xs text-brand-dimBlack">{{ COMMON_CONSTANTS.IMPERSONATED_USER }}:</p>
        <div class="mt-1 rounded-xl bg-brand-mutedWhite p-3">
          <div class="flex min-w-0 flex-col text-left">
            <span class="truncate text-sm font-semibold text-brand-black">{{
              displayUserName()
            }}</span>
            @if (displayUserEmail()) {
              <span class="truncate text-xs font-normal text-brand-slateGrey">{{
                displayUserEmail()
              }}</span>
            }
          </div>
        </div>
      </div>
    }

    @if (isInternalPortalSidebar()) {
      <!-- Admin internal portal: Figma Search + customer nav + Admin accordion -->
      <ul class="flex flex-col gap-3 mt-3">
        <!-- Search -->
        <li class="relative">
          @if (partyImpersonationActive() && !isCollapsed && showCustomerSearchCollapse()) {
            <div
              class="flex h-12 w-full items-center gap-0 rounded-2xl text-sm font-medium transition-colors duration-200 ease-out shadow-none pl-4 pr-1"
              [ngClass]="{
                'bg-brand-black !text-white shadow-admin-pill':
                  adminSearchRowDark() && !themeService.isDarkMode(),
                'sidebar-active-gradient text-white shadow-admin-pill':
                  adminSearchRowDark() && themeService.isDarkMode(),
                'text-neutral-slate hover:bg-brand-mutedPrimaryRed hover:text-brand-primary':
                  !adminSearchRowDark(),
              }"
            >
              <a
                [routerLink]="['/', Route.SEARCH_USER]"
                routerLinkActive
                (click)="onInternalSearchNavClick()"
                class="flex min-h-12 min-w-0 flex-1 items-center gap-3 py-0 no-underline"
                [ngClass]="{
                  'bg-brand-black text-white': adminSearchRowDark() && !themeService.isDarkMode(),
                  'sidebar-active-gradient text-white':
                    adminSearchRowDark() && themeService.isDarkMode(),
                  'text-neutral-slate': !adminSearchRowDark(),
                }"
              >
                <img
                  [src]="
                    adminSearchRowDark() ? IMAGE_PATHS.SEARCH_WHITE_ICON : IMAGE_PATHS.SEARCH_ICON
                  "
                  class="shrink-0 object-contain h-5 w-5"
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                />
                <span
                  class="overflow-hidden whitespace-nowrap text-left max-w-none opacity-100"
                  [ngClass]="{
                    'text-white': adminSearchRowDark(),
                    'text-neutral-slate': !adminSearchRowDark(),
                  }"
                  >{{ SIDEBAR_CONST.searchUser }}</span
                >
              </a>
              @if (!adminSectionOpen()) {
                <button
                  type="button"
                  class="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl border-0 bg-transparent p-0 text-inherit transition-colors"
                  [attr.aria-expanded]="customerSearchGroupOpen()"
                  [attr.aria-label]="
                    customerSearchGroupOpen()
                      ? ARIA_LABELS.COLLAPSE_CUSTOMER_NAV
                      : ARIA_LABELS.EXPAND_CUSTOMER_NAV
                  "
                  (click)="toggleCustomerSearchGroup()"
                >
                  <img
                    [src]="IMAGE_PATHS.ARROW_DOWN"
                    [alt]="ALT_TEXT.ARROW_DOWN"
                    class="pointer-events-none h-4 w-4 object-contain transition-transform duration-200"
                    [ngClass]="{
                      'rotate-180': customerSearchGroupOpen(),
                      'brightness-0 invert': adminSearchRowDark(),
                    }"
                    loading="lazy"
                  />
                </button>
              }
            </div>
          } @else {
            <a
              [routerLink]="['/', Route.SEARCH_USER]"
              routerLinkActive
              (click)="onInternalSearchNavClick()"
              class="flex h-12 items-center w-full rounded-2xl text-sm font-medium transition-colors duration-200 ease-out shadow-none"
              [attr.title]="isCollapsed ? SIDEBAR_CONST.searchUser : null"
              [ngClass]="{
                'gap-0 justify-center px-0': isCollapsed,
                'gap-3 pl-4 pr-3': !isCollapsed,
                'bg-brand-black !text-white shadow-admin-pill':
                  adminSearchRowDark() && !themeService.isDarkMode(),
                'sidebar-active-gradient text-white shadow-admin-pill':
                  adminSearchRowDark() && themeService.isDarkMode(),
                'text-neutral-slate hover:bg-brand-mutedPrimaryRed hover:text-brand-primary':
                  !adminSearchRowDark(),
              }"
            >
              <img
                [src]="
                  adminSearchRowDark() ? IMAGE_PATHS.SEARCH_WHITE_ICON : IMAGE_PATHS.SEARCH_ICON
                "
                class="shrink-0 object-contain theme-icon"
                [ngClass]="isCollapsed ? 'h-7 w-7' : 'h-5 w-5'"
                alt=""
                aria-hidden="true"
                loading="lazy"
              />
              <span
                class="overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200 ease-out"
                [ngClass]="{
                  'max-w-0 opacity-0': isCollapsed,
                  'max-w-none opacity-100': !isCollapsed,
                  'text-white': adminSearchRowDark(),
                  'text-neutral-slate': !adminSearchRowDark(),
                }"
                >{{ SIDEBAR_CONST.searchUser }}</span
              >
              @if (!isCollapsed && !adminSectionOpen() && showCustomerSearchCollapse()) {
                <span class="ms-auto flex shrink-0 items-center justify-center">
                  <img
                    [src]="IMAGE_PATHS.ARROW_DOWN"
                    [alt]="ALT_TEXT.ARROW_DOWN"
                    class="h-4 w-4 object-contain transition-transform duration-200"
                    [ngClass]="{
                      'brightness-0 invert': adminSearchRowDark(),
                    }"
                    loading="lazy"
                  />
                </span>
              }
            </a>
          }
        </li>

        @if (adminCustomerNavItems().length > 0) {
          @for (item of adminCustomerNavItems(); track item.route; let i = $index) {
            <li class="relative">
              <a
                [routerLink]="['/', item.route]"
                routerLinkActive="bg-brand-black !text-brand-white"
                [routerLinkActiveOptions]="{ exact: true }"
                #link="routerLinkActive"
                (click)="closeSidebar.emit()"
                (mouseenter)="onNavItemMouseEnter($event, i, item.label)"
                (mouseleave)="onNavItemMouseLeave()"
                class="flex items-center rounded-2xl text-sm font-medium text-neutral-slate transition-colors duration-200 ease-out"
                [attr.title]="isCollapsed ? item.label : null"
                [ngClass]="{
                  'sidebar-active-gradient text-white': link.isActive && themeService.isDarkMode(),
                  'hover:bg-brand-mutedPrimaryRed hover:text-brand-primary': !link.isActive,
                  'gap-0': isCollapsed,
                  'justify-center h-12 px-0': isCollapsed,
                  'gap-3': !isCollapsed,
                  'px-4 py-3': !isCollapsed,
                }"
              >
                <img
                  [src]="
                    link.isActive
                      ? item.iconActive
                      : hoveredIndex === i
                        ? item.iconHover
                        : item.icon
                  "
                  class="shrink-0 object-contain"
                  [ngClass]="isCollapsed ? 'h-7 w-7' : 'h-6 w-6'"
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                />
                <span
                  class="overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200 ease-out"
                  [ngClass]="isCollapsed ? 'max-w-0 opacity-0' : 'opacity-100'"
                  >{{ item.label }}</span
                >
              </a>
            </li>
          }
        }

        @if (showAdminAccordion()) {
          <!-- Admin accordion (Admin role only) -->
          <li class="relative">
            <button
              type="button"
              class="admin-sidebar-admin-trigger flex h-12 w-full items-center rounded-2xl text-sm font-medium transition-colors duration-200 ease-out text-left border-none cursor-pointer"
              [attr.title]="isCollapsed ? SIDEBAR_CONST.admin : null"
              [attr.aria-label]="SIDEBAR_CONST.admin"
              [attr.aria-expanded]="adminSectionOpen()"
              aria-controls="admin-sidebar-submenu"
              [ngClass]="{
                'gap-0 justify-center px-0': isCollapsed,
                'gap-3 pl-4 pr-3': !isCollapsed,
                'bg-brand-black !text-white shadow-admin-pill':
                  adminAccordionTriggerDark() && !themeService.isDarkMode(),
                'sidebar-active-gradient text-white shadow-admin-pill':
                  adminAccordionTriggerDark() && themeService.isDarkMode(),
                'text-neutral-slate hover:bg-brand-mutedPrimaryRed hover:text-brand-primary':
                  !adminAccordionTriggerDark(),
              }"
              (click)="toggleAdminSection()"
            >
              <img
                [src]="IMAGE_PATHS.PROFILE_DARK"
                class="shrink-0 object-contain theme-icon"
                [ngClass]="{
                  'h-7 w-7': isCollapsed,
                  'h-5 w-5': !isCollapsed,
                  'brightness-0 invert': adminAccordionTriggerDark(),
                }"
                alt=""
                aria-hidden="true"
                loading="lazy"
              />
              <span
                class="overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200 ease-out"
                [ngClass]="isCollapsed ? 'max-w-0 opacity-0' : 'max-w-none opacity-100'"
                >{{ SIDEBAR_CONST.admin }}</span
              >
              @if (!isCollapsed) {
                <span class="ms-auto flex shrink-0">
                  <img
                    [src]="IMAGE_PATHS.ARROW_DOWN"
                    [alt]="ALT_TEXT.ARROW_DOWN"
                    class="h-4 w-4 object-contain transition-transform duration-200"
                    [ngClass]="{
                      'rotate-180': adminSectionOpen(),
                      'brightness-0 invert': adminAccordionTriggerDark(),
                    }"
                    loading="lazy"
                  />
                </span>
              }
            </button>

            @if (adminSectionOpen()) {
              <ul id="admin-sidebar-submenu" class="flex flex-col gap-3 ps-0 list-none m-0 mt-5">
                @for (item of adminSubMenuItems(); track item.route; let subIdx = $index) {
                  <li class="relative">
                    <a
                      [routerLink]="['/', adminSubMenuRoute(item)]"
                      routerLinkActive
                      [routerLinkActiveOptions]="{ exact: false }"
                      #subLink="routerLinkActive"
                      (click)="closeSidebar.emit()"
                      (mouseenter)="onNavItemMouseEnter($event, subIdx, item.label)"
                      (mouseleave)="onNavItemMouseLeave()"
                      class="admin-sidebar-sublink flex items-center rounded-2xl text-sm font-medium text-neutral-slate transition-colors duration-200 ease-out"
                      [attr.title]="isCollapsed ? item.label : null"
                      [ngClass]="{
                        'bg-brand-black !text-brand-white':
                          isAdminSubMenuItemActive(item, subLink.isActive) &&
                          isCollapsed &&
                          !themeService.isDarkMode(),
                        'sidebar-active-gradient !text-white':
                          isAdminSubMenuItemActive(item, subLink.isActive) &&
                          isCollapsed &&
                          themeService.isDarkMode(),
                        'admin-sidebar-sublink-active':
                          isAdminSubMenuItemActive(item, subLink.isActive) &&
                          !isCollapsed &&
                          !themeService.isDarkMode(),
                        '!bg-brand-mutedPrimaryRed !text-white':
                          isAdminSubMenuItemActive(item, subLink.isActive) &&
                          !isCollapsed &&
                          themeService.isDarkMode(),
                        'hover:bg-brand-mutedPrimaryRed hover:text-brand-primary':
                          !isAdminSubMenuItemActive(item, subLink.isActive),
                        'gap-0 justify-center h-12 px-0 min-h-12': isCollapsed,
                        'min-h-[38px] gap-3 pl-4 pr-3': !isCollapsed,
                      }"
                    >
                      <img
                        [src]="
                          adminSubMenuIcon(
                            item,
                            subIdx,
                            isAdminSubMenuItemActive(item, subLink.isActive)
                          )
                        "
                        class="shrink-0 object-contain theme-icon"
                        [ngClass]="isCollapsed ? 'h-7 w-7' : 'h-5 w-5'"
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                      />
                      <span
                        class="min-w-0 overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200 ease-out"
                        [ngClass]="isCollapsed ? 'max-w-0 opacity-0' : 'opacity-100'"
                        >{{ item.label }}</span
                      >
                    </a>
                  </li>
                }
              </ul>
            }
          </li>
        }
      </ul>
    } @else {
      <ul class="flex flex-col gap-3">
        @for (item of sidebarItems(); track item.route; let i = $index) {
          <li class="relative">
            <a
              [routerLink]="['/', item.route]"
              routerLinkActive="bg-brand-black !text-brand-white"
              [routerLinkActiveOptions]="{ exact: true }"
              #link="routerLinkActive"
              (click)="closeSidebar.emit()"
              (mouseenter)="onNavItemMouseEnter($event, i, item.label)"
              (mouseleave)="onNavItemMouseLeave()"
              class="flex items-center rounded-2xl text-sm font-medium text-neutral-slate transition-colors duration-200 ease-out"
              [attr.title]="isCollapsed ? item.label : null"
              [ngClass]="{
                'sidebar-active-gradient !text-white': link.isActive && themeService.isDarkMode(),
                'hover:bg-brand-mutedPrimaryRed hover:text-brand-primary': !link.isActive,
                'gap-0': isCollapsed,
                'justify-center h-12 px-0': isCollapsed,
                'gap-3': !isCollapsed,
                'px-4 py-3': !isCollapsed,
              }"
            >
              <img
                [src]="
                  link.isActive ? item.iconActive : hoveredIndex === i ? item.iconHover : item.icon
                "
                class="shrink-0 object-contain theme-icon"
                [ngClass]="isCollapsed ? 'h-7 w-7' : 'h-6 w-6'"
                alt=""
                aria-hidden="true"
                loading="lazy"
              />
              <span
                class="overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200 ease-out"
                [ngClass]="isCollapsed ? 'max-w-0 opacity-0' : 'opacity-100'"
                >{{ item.label }}</span
              >
            </a>
          </li>
        }
      </ul>
    }

    <!-- Navigation – mobile only ---- Hover will not work on this -->
    <ul class="flex flex-col gap-3 md:hidden pt-3">
      @for (item of sidebarHeaderItems; track item.route) {
        <li>
          @if (item.route === Route.LOG_OUT) {
            <button
              type="button"
              class="flex w-full items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-neutral-slate hover:bg-brand-mutedPrimaryRed hover:text-brand-primary bg-transparent border-none cursor-pointer text-left"
              (click)="onLogout()"
            >
              <img
                [src]="item.icon"
                class="h-6 w-6 shrink-0 object-contain theme-icon"
                alt=""
                aria-hidden="true"
                loading="lazy"
              />

              {{ item.label }}
            </button>
          } @else if (item.route === Route.DARK_MODE) {
            <button
              type="button"
              class="flex w-full items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-neutral-slate hover:bg-brand-mutedPrimaryRed hover:text-brand-primary bg-transparent border-none cursor-pointer text-left"
              (click)="onDarkMode()"
            >
              <img
                [src]="item.icon"
                class="h-6 w-6 shrink-0 object-contain theme-icon"
                alt=""
                aria-hidden="true"
                loading="lazy"
              />
              {{ themeService.label() }}
            </button>
          } @else {
            <a
              [routerLink]="['/', item.route]"
              routerLinkActive="bg-brand-black !text-brand-white"
              [routerLinkActiveOptions]="{ exact: true }"
              #link="routerLinkActive"
              (click)="closeSidebar.emit()"
              class="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-neutral-slate hover:bg-brand-mutedPrimaryRed hover:text-brand-primary"
              [ngClass]="{
                'sidebar-active-gradient  !text-white': link.isActive && themeService.isDarkMode(),
              }"
            >
              <img
                [src]="
                  link.isActive
                    ? themeService.isDarkMode()
                      ? item.icon
                      : item.iconActive
                        ? item.iconActive
                        : item.icon
                    : item.icon
                "
                class="h-6 w-6 shrink-0 object-contain theme-icon"
                alt=""
                aria-hidden="true"
                loading="lazy"
              />

              {{ item.label }}
            </a>
          }
        </li>
      }
    </ul>
  </nav>

  @if (isCollapsed && collapsedTooltipVisible()) {
    <span
      class="pointer-events-none fixed z-[220] hidden h-auto -translate-y-1/2 items-center whitespace-nowrap rounded-full bg-brand-black px-6 py-3 text-sm font-semibold text-brand-white shadow-sidebar md:flex"
      [style.left.px]="collapsedTooltipLeft()"
      [style.top.px]="collapsedTooltipTop()"
    >
      {{ collapsedTooltipLabel() }}
    </span>
  }
</aside>
